import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Music, AudioWaveform as Waveform, Brain, Sliders } from 'lucide-react';
import { AudioUploader } from './components/AudioUploader';
import { WaveformVisualizer } from './components/WaveformVisualizer';
import { ProcessingStatus } from './components/ProcessingStatus';

export default function App() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedUrl, setProcessedUrl] = useState<string | null>(null);
  const [processingProgress, setProcessingProgress] = useState(0);

  return (
    <div className="min-h-screen bg-gray-900 animated-bg">
      {/* Header */}
      <div className="fixed top-0 left-0 z-50 p-4 flex items-center justify-between w-full">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-2 glass-panel px-4 py-2"
        >
          <Music className="w-6 h-6 text-blue-400" />
          <span className="font-bold text-lg text-blue-100">Famous Artist Remastered</span>
        </motion.div>
        
        {/* Free Badge */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="glass-panel px-4 py-2 bg-green-500/20 border border-green-500/30"
        >
          <span className="text-green-400 font-semibold">100% Free</span>
        </motion.div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1519419166318-4f5c601b8e6c?auto=format&fit=crop&q=80"
            alt="Studio background"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold text-blue-100 mb-6"
          >
            Free AI Audio
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400"> Mastering</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto mb-8"
          >
            Transform your music with cutting-edge AI technology. Professional-grade mastering that learns and adapts to your unique sound - completely free, no strings attached.
          </motion.p>
          
          {/* Feature Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="flex flex-wrap justify-center gap-4 mb-8"
          >
            <div className="glass-panel px-4 py-2 bg-blue-500/20 border border-blue-500/30">
              <span className="text-blue-300">✨ No Account Required</span>
            </div>
            <div className="glass-panel px-4 py-2 bg-purple-500/20 border border-purple-500/30">
              <span className="text-purple-300">🎵 Unlimited Processing</span>
            </div>
            <div className="glass-panel px-4 py-2 bg-cyan-500/20 border border-cyan-500/30">
              <span className="text-cyan-300">🚀 Instant Results</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="glass-panel p-6 space-y-6">
          <AudioUploader 
            onProcessingStart={() => setIsProcessing(true)}
            onProcessingComplete={(url) => {
              setProcessedUrl(url);
              setIsProcessing(false);
            }}
            onProgress={setProcessingProgress}
          />

          {processedUrl && (
            <div className="space-y-4">
              <WaveformVisualizer 
                audioUrl={processedUrl}
                isProcessing={isProcessing}
              />
            </div>
          )}

          {isProcessing && (
            <ProcessingStatus 
              isProcessing={isProcessing}
              progress={processingProgress}
            />
          )}
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard
            icon={Waveform}
            title="AI-Driven Mastering"
            description="Deep learning models analyze song structure and characteristics for organic, human-like mastering results."
          />
          <FeatureCard
            icon={Brain}
            title="Neural Processing"
            description="Advanced neural networks enhance audio quality while preserving musical dynamics."
          />
          <FeatureCard
            icon={Sliders}
            title="Adaptive EQ"
            description="Intelligent equalization that adapts to your music's frequency content and genre."
          />
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900/50 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <p className="text-gray-400">
            Famous Artist Remastered is a free service. No credit card required. No hidden fees.
          </p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }: {
  icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="glass-panel glass-panel-hover"
    >
      <div className="flex items-center gap-4 mb-4">
        <div className="p-3 bg-blue-500/10 rounded-lg">
          <Icon className="w-6 h-6 text-blue-400" />
        </div>
        <h3 className="text-xl font-semibold text-blue-100">{title}</h3>
      </div>
      <p className="text-gray-300 leading-relaxed">{description}</p>
    </motion.div>
  );
}